import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition, RadioGroup } from '@headlessui/react'
import { XMarkIcon, ShieldExclamationIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import type { ComplianceViolation, TenantPolicyWaiver, WaiverType, CreateWaiverRequest, UpdateWaiverRequest } from '@/types'

interface WaiverModalProps {
  isOpen: boolean
  onClose: () => void
  violation: ComplianceViolation | null
  onSubmit: (request: CreateWaiverRequest) => void
  isSubmitting?: boolean
  existingWaiver?: TenantPolicyWaiver
  onUpdate?: (waiverId: string, request: UpdateWaiverRequest) => void
}

interface WaiverTypeOption {
  value: WaiverType
  label: string
  description: string
}

const waiverTypes: WaiverTypeOption[] = [
  {
    value: 'Permanent',
    label: 'Alternative Mitigation',
    description: 'This risk is addressed through an alternative control or compensating measure.',
  },
  {
    value: 'Temporary',
    label: 'Temporary Exception',
    description: 'We acknowledge the risk and will remediate by a specific date.',
  },
  {
    value: 'NotApplicable',
    label: 'Not Applicable',
    description: 'This control is not relevant to our organization or use case.',
  },
]

/**
 * Modal for adding a waiver/exception to a compliance violation.
 * Includes waiver type selection, reason text area, and expiration date for temporary waivers.
 */
export default function WaiverModal({
  isOpen,
  onClose,
  violation,
  onSubmit,
  isSubmitting = false,
  existingWaiver,
  onUpdate,
}: WaiverModalProps) {
  const isEditMode = !!existingWaiver
  const [waiverType, setWaiverType] = useState<WaiverType>('Permanent')
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens (isOpen changes to true) or violation changes
  // In edit mode, pre-fill from existing waiver data
  useEffect(() => {
    if (isOpen) {
      if (existingWaiver) {
        setWaiverType(existingWaiver.waiverType)
        setReason(existingWaiver.reason)
        setExpiresAt(existingWaiver.expiresAt ? existingWaiver.expiresAt.split('T')[0] : '')
      } else {
        setWaiverType('Permanent')
        setReason('')
        setExpiresAt('')
      }
      setErrors({})
    }
  }, [isOpen, violation?.id, existingWaiver])

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!reason.trim()) {
      newErrors.reason = 'Please provide a reason for this waiver.'
    } else if (reason.trim().length < 20) {
      newErrors.reason = 'Please provide a more detailed reason (at least 20 characters).'
    }

    if (waiverType === 'Temporary') {
      if (!expiresAt) {
        newErrors.expiresAt = 'Please select an expiration date for temporary waivers.'
      } else {
        const expDate = new Date(expiresAt)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (expDate <= today) {
          newErrors.expiresAt = 'Expiration date must be in the future.'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = () => {
    if (!violation || !validate()) return

    if (isEditMode && existingWaiver && onUpdate) {
      onUpdate(existingWaiver.id, {
        waiverType,
        reason: reason.trim(),
        ...(waiverType === 'Temporary' && { expiresAt }),
      })
    } else {
      const request: CreateWaiverRequest = {
        policyId: violation.policyId,
        waiverType,
        reason: reason.trim(),
        ...(waiverType === 'Temporary' && { expiresAt }),
      }

      onSubmit(request)
    }
  }

  // Get minimum date for expiration (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  if (!violation) return null

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-dark-800 border border-dark-700 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-dark-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/15">
                      <ShieldExclamationIcon className="h-5 w-5 text-amber-400" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-slate-100">
                      {isEditMode ? 'Edit Waiver' : 'Add Waiver'}
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Violation info */}
                <div className="mt-4 p-4 bg-dark-900/50 rounded-xl border border-dark-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-mono text-slate-500">{violation.policyCode}</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                        violation.criticality === 'SHALL'
                          ? 'bg-red-500/15 text-red-400 border-red-500/30'
                          : violation.criticality === 'SHOULD'
                          ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                          : 'bg-slate-500/15 text-slate-400 border-slate-500/30'
                      }`}
                    >
                      {violation.criticality}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-200">{violation.policyTitle}</p>
                  <p className="text-xs text-slate-400 mt-1">{violation.message}</p>
                </div>

                {/* Waiver type selection */}
                <div className="mt-6">
                  <label className="text-sm font-medium text-slate-300 block mb-3">
                    Waiver Type
                  </label>
                  <RadioGroup value={waiverType} onChange={setWaiverType} className="space-y-2">
                    {waiverTypes.map((option) => (
                      <RadioGroup.Option
                        key={option.value}
                        value={option.value}
                        className={({ checked }) =>
                          `relative flex cursor-pointer rounded-xl border p-4 transition-colors ${
                            checked
                              ? 'border-primary-500 bg-primary-500/10'
                              : 'border-dark-600 bg-dark-900/50 hover:border-dark-500'
                          }`
                        }
                      >
                        {({ checked }) => (
                          <div className="flex w-full items-center justify-between">
                            <div>
                              <RadioGroup.Label
                                as="span"
                                className="text-sm font-medium text-slate-200"
                              >
                                {option.label}
                              </RadioGroup.Label>
                              <RadioGroup.Description
                                as="span"
                                className="block text-xs text-slate-500 mt-1"
                              >
                                {option.description}
                              </RadioGroup.Description>
                            </div>
                            {checked && (
                              <CheckCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                            )}
                          </div>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </RadioGroup>
                </div>

                {/* Expiration date for temporary waivers */}
                {waiverType === 'Temporary' && (
                  <div className="mt-4">
                    <label
                      htmlFor="expiresAt"
                      className="text-sm font-medium text-slate-300 block mb-2"
                    >
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      id="expiresAt"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      min={getMinDate()}
                      className={`w-full px-4 py-2 bg-dark-900 border rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 ${
                        errors.expiresAt
                          ? 'border-red-500/50 focus:border-red-500/50'
                          : 'border-dark-600 focus:border-primary-500/50'
                      }`}
                    />
                    {errors.expiresAt && (
                      <p className="text-xs text-red-400 mt-1">{errors.expiresAt}</p>
                    )}
                  </div>
                )}

                {/* Reason text area */}
                <div className="mt-4">
                  <label htmlFor="reason" className="text-sm font-medium text-slate-300 block mb-2">
                    Reason / Justification
                  </label>
                  <textarea
                    id="reason"
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Explain why this policy should be waived for your organization..."
                    className={`w-full px-4 py-2 bg-dark-900 border rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-none ${
                      errors.reason
                        ? 'border-red-500/50 focus:border-red-500/50'
                        : 'border-dark-600 focus:border-primary-500/50'
                    }`}
                  />
                  {errors.reason && <p className="text-xs text-red-400 mt-1">{errors.reason}</p>}
                  <p className="text-xs text-slate-500 mt-1">
                    {reason.length}/20 minimum characters
                  </p>
                </div>

                {/* Warning for SHALL policies */}
                {violation.criticality === 'SHALL' && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-400">
                      <strong>Warning:</strong> This is a critical (SHALL) requirement. Waiving this
                      policy may significantly impact your security posture and compliance status.
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-dark-700 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-slate-300 bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : isEditMode ? 'Update Waiver' : 'Add Waiver'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
